import { getScaledValue, getPercentageOfMax } from '@/helpers/helpers'
import { countBy } from 'lodash'
import { getGroupedActions, getRaiseGroupedStrategyEvEq } from '@/helpers/grouping'
import { getAvailableActions, getHistorySpots } from '@/store/gamestate/gamestate'

import { getCategoriesColors } from '@/helpers/constants'
import { MAX_NUM_OF_PLAYERS } from '@/helpers/constantsNew'
import allCombos from '@/helpers/allCombos.json'
import allCombosGroupsIdxs from '@/helpers/allCombosGroupsIdxs.json'
import allCombosSummaryIdxs from '@/helpers/allCombosSummaryIdxs.json'

const heroIndex = 0
function getAbsoluteEvRatio(absoluteRatio) {
  if (absoluteRatio > 1) {
    absoluteRatio = 1
  }
  if (absoluteRatio < -1) {
    absoluteRatio = -1
  }
  absoluteRatio *= 100
  absoluteRatio += 100
  return absoluteRatio
}
function getEvDistanceFromAvgEv(ev, average_ev) {
  const res = Math.abs(ev - average_ev)
  if (ev < average_ev) {
    return res * -1
  }
  return res
}
function getEvClr({ total_ev, ev, clrScale = 10 }) {
  const distance = getEvDistanceFromAvgEv(ev, total_ev)
  let absoluteRatio = Math.abs(distance) / Math.abs(total_ev || 1)
  if (ev < total_ev) {
    absoluteRatio *= -1
  }
  absoluteRatio = getAbsoluteEvRatio(absoluteRatio)
  let gradient = Math.round(absoluteRatio / clrScale) * clrScale
  gradient *= 0.5

  const color = `--clr-eveqeqrbl${gradient}`
  return color
}
function getEqClr({ eq, clrScale = 5 }) {
  const gradient = Math.round((eq * 100) / clrScale) * clrScale
  const color = `--clr-eveqeqrbl${gradient}`
  return color
}
function eqrClr({ eqr, clrScale = 5 }) {
  const gradient = Math.round((eqr * 100) / clrScale) * clrScale
  const color = `--clr-eveqeqrbl${gradient}`
  return color
}
function getEvScaledClr({ totalEv, value, min, max }) {
  const ev = getScaledValue({ min, max, value })
  totalEv = getScaledValue({ min, max, value: totalEv })
  const color = getEvClr({ total_ev: totalEv, ev, clrScale: 50 })
  return color
}
function getScaledClr({ value, min, max, clrScale = 25 }) {
  const v = getScaledValue({ min, max, value })
  const gradient = Math.round((v * 100) / clrScale) * clrScale
  const color = `--clr-eveqeqrbl${gradient}`
  return color
}
function getEvCompareByPot({ ev_difference, pot }) {
  const valByPot = ev_difference / pot
  let positiveVal = valByPot < -0.05 ? -0.05 : valByPot > 0.05 ? 0.05 : valByPot
  positiveVal += 0.05
  const scaledVal = getScaledValue({ min: 0, max: 0.1, value: positiveVal })
  return scaledVal
}
function getEvCompareClr({ eq_compare, clrScale = 10 }) {
  const gradient = Math.round((eq_compare * 100) / clrScale) * clrScale
  const color = `--clr-rwg${gradient}`
  // const color = `--clr-eveqeqrbl${gradient}`
  return color
}
function getEvTopAction({ actions }) {
  const biggest_ev = Math.max.apply(
    0,
    actions.map(a => a.ev),
  )
  const res = actions.find(a => a.ev === biggest_ev)
  return res
}
function getStrategyClrs({ solution, range }) {
  const raHeight = `${range * 100}%`
  const res = solution.reduce(
    (a, b) => {
      const clr = b.css_class
      const { hex } = b
      const p = a.p + b.strategy * 100
      const r = {
        clrs_var: `${a.clrs_var}linear-gradient(to right, var(--clr-${clr}), var(--clr-${clr})),`,
        clrs_hex: `${a.clrs_hex}linear-gradient(to right, ${hex}, ${hex}),`,
        size: `${a.size}${p}% ${raHeight},`,
        // size_vertical: `${a.size_vertical} 100% ${p}%`,
        p,
      }
      return r
    },
    {
      clrs_hex: '',
      clrs_var: '',
      size: '',
      size_vertical: '',
      position_x: [],
      p: 0,
    },
  )
  res.size = res.size.slice(0, -1)
  // res.size_vertical = res.size_vertical.slice(0, -1)
  res.clrs_hex = res.clrs_hex.slice(0, -1)
  res.clrs_var = res.clrs_var.slice(0, -1)
  return res
}
function getComboStrategyClrs({ solution, range = 1, strategyKey = 'strategy' }) {
  let style = {
    hex: '',
    vars: '',
  }
  // var(--clr-range_empty)
  // var(--clr-black-1)
  // var(--clr-range_active)
  const somboSolution = solution
  const solLength = somboSolution.length
  const first = somboSolution[0]
  if (somboSolution[0]) {
    let clr = first.css_class
    let { hex } = first
    let p = first[strategyKey] * range * 100
    const clrs_vars = `linear-gradient(to top, var(--clr-${clr}) ${p}%,`
    const clrs_hex = `linear-gradient(to top, ${hex} ${p}%,`

    if (solLength === 1) {
      const styleVars = `background-image: ${clrs_vars} var(--clr-${clr}) ${p}%, var(--clr-range_active) ${p}%);`
      const styleHex = `background-image: ${clrs_hex} ${hex} ${p}%, var(--clr-range_active) ${p}%);`
      style = {
        hex: styleHex,
        vars: styleVars,
      }
      return style
    }

    const inBetween = somboSolution.slice(1, -1)
    const res = inBetween.reduce(
      (a, b) => {
        clr = b.css_class
        hex = b.hex
        p = a.p + b[strategyKey] * range * 100
        const r = {
          clrs_vars: `${a.clrs_vars} var(--clr-${clr}) ${a.p}%, var(--clr-${clr}) ${p}%,`,
          clrs_hex: `${a.clrs_hex} ${hex} ${a.p}%, ${hex} ${p}%,`,
          p,
        }
        return r
      },
      { clrs_vars, clrs_hex, p },
    )
    const last = somboSolution[solLength - 1]
    clr = last.css_class
    hex = last.hex
    p = res.p + last[strategyKey] * range * 100

    const styleVars = `background-image: ${res.clrs_vars} var(--clr-${clr}) ${res.p}%, var(--clr-${clr}) ${p}%, var(--clr-range_active) ${p}%);`
    const styleHex = `background-image: ${res.clrs_hex} ${hex} ${res.p}%, ${hex} ${p}%, var(--clr-range_active) ${p}%);`
    style = {
      hex: styleHex,
      vars: styleVars,
    }
  }

  return style
}
function getComboEvCompareClr({
  combo,
  grouping,
  action_primary,
  action_secondary,
  compare_by_best_ev_action,
  pot,
}) {
  let groupedActions = combo.solution
  if (grouping !== 'swv_grouping_none') {
    groupedActions = getGroupedActions({
      actions: combo.solution,
      grouping,
      groupValuesFunction: getRaiseGroupedStrategyEvEq,
      raisesFirst: true,
    })
  }

  const aa_primary = groupedActions[action_primary.order]
  let aa_secondary = groupedActions[action_secondary.order]

  if (compare_by_best_ev_action) {
    aa_secondary = getEvTopAction({ actions: groupedActions })
  }

  const ev_difference = aa_primary?.ev - aa_secondary?.ev
  const scaledVal = getEvCompareByPot({ ev_difference, pot })
  const clr = getEvCompareClr({ eq_compare: scaledVal, clrScale: 10 })
  return clr
}
function getEvCompareClrs({
  combos_in_range,
  study_ev_comparison,
  grouping,
  range,
  // combos,
}) {
  const { pot, action_primary, action_secondary, compare_by_best_ev_action } = study_ev_comparison

  // Formula
  // (action_ev1 - action_ev2) / pot
  // -5% red(0)  — 5% green(100)

  const raHeight = `${range * 100}%`
  const pIncrement = 1 / combos_in_range.length

  const res = combos_in_range.reduce(
    (a, b) => {
      const clr = getComboEvCompareClr({
        combo: b,
        grouping,
        action_primary,
        action_secondary,
        compare_by_best_ev_action,
        pot,
      })

      const p = a.p + pIncrement * 100
      return {
        clrs: `${a.clrs}linear-gradient(to right, var(${clr}), var(${clr})),`,
        size: `${a.size}${p}% ${raHeight},`,
        p,
      }
    },
    { clrs: '', size: '', p: 0 },
  )
  res.clrs = res.clrs.slice(0, -1)
  res.size = res.size.slice(0, -1)
  // console.log(res)
  return res
}
function getEvClrs({ total_ev, combos_in_range, player, range }) {
  const raHeight = `${range * 100}%`
  const pIncrement = 1 / combos_in_range.length

  const res = combos_in_range.reduce(
    (a, b) => {
      const l_player = b.players?.[player]
      const ev = l_player?.ev
      const clr = getEvClr({ total_ev, ev, clrScale: 10 })
      const p = a.p + pIncrement * 100
      return {
        clrs: `${a.clrs}linear-gradient(to right, var(${clr}), var(${clr})),`,
        size: `${a.size}${p}% ${raHeight},`,
        p,
      }
    },
    { clrs: '', size: '', p: 0 },
  )
  res.clrs = res.clrs.slice(0, -1)
  res.size = res.size.slice(0, -1)
  return res
}
function getEqClrs({ combos_in_range, player, range }) {
  const raHeight = `${range * 100}%`
  const pIncrement = 1 / combos_in_range.length

  const res = combos_in_range.reduce(
    (a, b) => {
      const eq = b.players?.[player]?.eq
      const clr = Math.round((eq * 100) / 5) * 5
      const p = a.p + pIncrement * 100
      return {
        clrs: `${a.clrs}linear-gradient(to right, var(--clr-eveqeqrbl${clr}), var(--clr-eveqeqrbl${clr})),`,
        size: `${a.size}${p}% ${raHeight},`,
        p,
      }
    },
    {
      clrs: '',
      size: '',
      p: 0,
    },
  )
  res.clrs = res.clrs.slice(0, -1)
  res.size = res.size.slice(0, -1)
  return res
}
function getEqrClrs({ combos_in_range, player, range }) {
  const raHeight = `${range * 100}%`
  const pIncrement = 1 / combos_in_range.length

  const res = combos_in_range.reduce(
    (a, b) => {
      const eqr = b.players?.[player]?.eqr_clrscaled
      const clr = Math.round((eqr * 100) / 5) * 5
      const p = a.p + pIncrement * 100
      return {
        clrs: `${a.clrs}linear-gradient(to right, var(--clr-eveqeqrbl${clr}), var(--clr-eveqeqrbl${clr})),`,
        size: `${a.size}${p}% ${raHeight},`,
        p,
      }
    },
    {
      clrs: '',
      size: '',
      p: 0,
    },
  )
  res.clrs = res.clrs.slice(0, -1)
  res.size = res.size.slice(0, -1)
  return res
}
function getBlockerClrs({ key = 'blocker_rate', combos_in_range, player, range }) {
  const raHeight = `${range * 100}%`
  const pIncrement = 1 / combos_in_range.length

  const res = combos_in_range.reduce(
    (a, b) => {
      const value = b.players?.[player]?.[key]
      const idx = (value - 1) * 10
      const p = a.p + pIncrement * 100
      return {
        clrs: `${a.clrs}linear-gradient(to right, var(--clr-eveqeqrbl${idx}), var(--clr-eveqeqrbl${idx})),`,
        size: `${a.size}${p}% ${raHeight},`,
        p,
      }
    },
    { clrs: '', size: '', p: 0 },
  )
  res.clrs = res.clrs.slice(0, -1)
  res.size = res.size.slice(0, -1)
  return res
}

function getSuitsHands({
  suited, //
  offsuited,
  exclude_suited,
  exclude_offsuited,
}) {
  let res_suited = null
  let res_offsuited = null

  const all_suited = allCombos.filter(combo => combo[1] === combo[3])
  const all_offsuited = allCombos.filter(combo => combo[1] !== combo[3])

  if (suited.length) {
    res_suited = all_suited.filter(combo => suited.some(ss => combo[1] === ss && combo[3] === ss))
  }
  if (exclude_suited.length) {
    res_suited = (res_suited || all_suited).filter(
      combo => !exclude_suited.some(ss => combo[1] === ss && combo[3] === ss),
    )
  }

  if (offsuited.length) {
    res_offsuited = all_offsuited.filter(combo =>
      offsuited.some(ss => combo[1] !== combo[3] && (combo[1] === ss || combo[3] === ss)),
    )
  }
  if (exclude_offsuited.length) {
    res_offsuited = (res_offsuited || all_offsuited).filter(
      combo =>
        !exclude_offsuited.some(
          ss => combo[1] !== combo[3] && (combo[1] === ss || combo[3] === ss),
        ),
    )
  }

  const res = [...new Set([...(res_suited || []), ...(res_offsuited || [])])]

  // console.log({
  //   res_suited,
  //   res_offsuited,
  //   res,
  //   suited,
  //   offsuited,
  //   exclude_suited,
  //   exclude_offsuited,
  // })

  return res
}

// prettier-ignore
function rangeTableGroups() {
  return [
    'AA', 'AKs', 'AQs', 'AJs', 'ATs', 'A9s', 'A8s', 'A7s', 'A6s', 'A5s', 'A4s', 'A3s', 'A2s',
    'AKo', 'KK', 'KQs', 'KJs', 'KTs', 'K9s', 'K8s', 'K7s', 'K6s', 'K5s', 'K4s', 'K3s', 'K2s',
    'AQo', 'KQo', 'QQ', 'QJs', 'QTs', 'Q9s', 'Q8s', 'Q7s', 'Q6s', 'Q5s', 'Q4s', 'Q3s', 'Q2s',
    'AJo', 'KJo', 'QJo', 'JJ', 'JTs', 'J9s', 'J8s', 'J7s', 'J6s', 'J5s', 'J4s', 'J3s', 'J2s',
    'ATo', 'KTo', 'QTo', 'JTo', 'TT', 'T9s', 'T8s', 'T7s', 'T6s', 'T5s', 'T4s', 'T3s', 'T2s',
    'A9o', 'K9o', 'Q9o', 'J9o', 'T9o', '99', '98s', '97s', '96s', '95s', '94s', '93s', '92s',
    'A8o', 'K8o', 'Q8o', 'J8o', 'T8o', '98o', '88', '87s', '86s', '85s', '84s', '83s', '82s',
    'A7o', 'K7o', 'Q7o', 'J7o', 'T7o', '97o', '87o', '77', '76s', '75s', '74s', '73s', '72s',
    'A6o', 'K6o', 'Q6o', 'J6o', 'T6o', '96o', '86o', '76o', '66', '65s', '64s', '63s', '62s',
    'A5o', 'K5o', 'Q5o', 'J5o', 'T5o', '95o', '85o', '75o', '65o', '55', '54s', '53s', '52s',
    'A4o', 'K4o', 'Q4o', 'J4o', 'T4o', '94o', '84o', '74o', '64o', '54o', '44', '43s', '42s',
    'A3o', 'K3o', 'Q3o', 'J3o', 'T3o', '93o', '83o', '73o', '63o', '53o', '43o', '33', '32s',
    'A2o', 'K2o', 'Q2o', 'J2o', 'T2o', '92o', '82o', '72o', '62o', '52o', '42o', '32o', '22',
  ]
}

function getComboMainGroup({ id }) {
  // diagonal  i[0] === i[2] && i[1] !== i[3] - AhAc
  // suit  i[0] !== i[2] && i[1] === i[3]  - AhDh
  // offsuit  i[0] !== i[2] && i[1] !== i[3] - AhDc
  const main_group =
    id[0] === id[2] && id[1] !== id[3]
      ? 'diagonal'
      : id[0] !== id[2] && id[1] === id[3]
        ? 'suit'
        : 'offsuit'

  const groupId = id[0] + id[2]
  const group_id =
    main_group === 'diagonal' ? groupId : main_group === 'suit' ? `${groupId}s` : `${groupId}o`

  return { main_group, group_id }
  // return { main_group, group_id, group_id_short: groupId }
}
function combosSkeleton() {
  function getGroupSkeleton() {
    return {
      range: 0,
      range_normalized: 0,
      range_full_height: 0,
      ev: 0,
      eq: 0,
      eqr: 0,
      blocker_rate: 0,
      unblocker_rate: 0,
      eqr_clrscaled: 0,
      default: {
        range: 0,
        range_normalized: 0,
        range_full_height: 0,
      },
    }
  }
  function getAllGroupSkeleton() {
    return Array.from({ length: MAX_NUM_OF_PLAYERS }, () => getGroupSkeleton())
  }

  return allCombos.map((cp, idx) => {
    const { main_group, group_id } = getComboMainGroup({ id: cp })
    // const { main_group, group_id, group_id_short } = getComboMainGroup({ id: cp })
    const r = {
      main_group,
      group_id,
      // group_id_short,
      id: cp,
      api_index: idx,
      summary_idx: allCombosSummaryIdxs[idx],
      solution: [],
      is_locked: false,
      players: getAllGroupSkeleton(),
    }
    return r
  })
}

function rangesDataSkeleton() {
  const combos = combosSkeleton()

  function getPlayerSkeleton() {
    return {
      range: 0,
      range_normalized: 0,
      range_full_height: 0,
      ev: 0,
      eq: 0,
      eqr: 0,
      default: {
        range: 0,
        range_normalized: 0,
        range_full_height: 0,
        ev: 0,
        eq: 0,
        eqr: 0,
        total_combos_available: 0,
      },
    }
  }

  function getAllPlayersSkeleton() {
    return Array.from({ length: MAX_NUM_OF_PLAYERS }, () => getPlayerSkeleton())
  }

  const tableGroups = rangeTableGroups().map((group, gIdx) => {
    let sortedCombos = []

    const groupCombos = combos.filter(i => i.group_id === group)

    const { main_group } = groupCombos[0]
    if (main_group === 'offsuit') {
      // combosLength = 12
      sortedCombos = [
        groupCombos[11],
        groupCombos[10],
        groupCombos[9],
        groupCombos[8],
        groupCombos[7],
        groupCombos[6],
        groupCombos[5],
        groupCombos[4],
        groupCombos[3],
        groupCombos[2],
        groupCombos[1],
        groupCombos[0],
      ]
    } else if (main_group === 'suit') {
      // combosLength = 4
      sortedCombos = [groupCombos[3], groupCombos[2], groupCombos[1], groupCombos[0]]
    } else {
      // combosLength = 6
      sortedCombos = [
        groupCombos[5],
        groupCombos[4],
        groupCombos[2],
        groupCombos[3],
        groupCombos[1],
        groupCombos[0],
      ]
    }
    const res = {
      id: group,
      main_group,
      hand_count: main_group === 'offsuit' ? 12 : main_group === 'suit' ? 4 : 6,
      // id_short: group[0] + group[1],
      table_index: gIdx,
      combos: sortedCombos,
      solution: [],
      is_locked: false,
      players: getAllPlayersSkeleton(),
    }
    return res
  })
  return { combos, tableGroups }
}

function getActionPick(a) {
  const isRaise = a.type === 'RAISE'
  return {
    code: a.code,
    type: a.type,
    display_name: a.display_name,
    hex: a.hex,
    css_class: a.css_class,
    order: a.order,
    order_reverse: a.order_reverse,
    // ev: a.ev,
    // eq: a.eq,
    // eqr: a.eqr,
    // strategy: a.strategy,
    ...(isRaise ? { raise_alias: a.raise_alias } : {}),
    ...(isRaise ? { raise_amount: a.raise_amount } : {}),
    ...(isRaise ? { amount_by_pot: a.amount_by_pot } : {}),
    ...(isRaise ? { raise_group: a.raise_group } : {}),
    ...(isRaise ? { allin: a.allin } : {}),
  }
}
function getAvailableActionsFromApiSolutions({ solData }) {
  const solution = solData.solutions || []
  let aa = getAvailableActions({
    available_actions: solution.map(i => i.action),
    game: solData.game || null,
    selected_action_code: 'unset',
  })
  aa = JSON.parse(JSON.stringify(aa)).reverse()
  return aa
}
function getSolutionPick({ solution }) {
  const pickedSolution = solution.map(a => {
    const res = {
      ...getActionPick(a),
      total_frequency: a.total_frequency,
      draw_categories: a.draw_categories,
      hand_categories: a.hand_categories,
      equity_buckets: a.equity_buckets,
      equity_buckets_advanced: a.equity_buckets_advanced,
      total_combos: a.total_combos,
      total_combos_default: a.total_combos,
    }
    return res
  })
  // console.log(pickedSolution)
  return pickedSolution
}

function getSolution({ solution, available_actions }) {
  const res = available_actions.map(aa => {
    const solAction = (solution || []).find(a => a.action.code === aa.code)
    const r = {
      ...(solAction.full_solution ? solAction.full_solution : {}),
      ...solAction,
      total_combos_default: solAction.total_combos,
      ...aa,
      // ...getActionPick(aa),
    }
    return r
  })
  // console.log(res)
  return res
}

const sanitizeToOne = range => Math.min(1, range)

const getMaxRange = ranges => sanitizeToOne(Math.max(...(ranges || [])))

function getPlayerTopHandGroupRange(simple_hand_counters) {
  const res = []
  Object.keys(simple_hand_counters || {}).forEach(groupName => {
    const range = simple_hand_counters?.[groupName]?.total_frequency
    const sanitizedRange = sanitizeToOne(range)
    res.push(sanitizedRange)
  })
  return res
}

function getSolPlayer({ player }) {
  const total_ev = player?.total_ev ?? null
  const total_eq = player?.total_eq ?? null
  const total_eqr = player?.total_eqr ?? null

  const range = player?.range || []

  const hand_evs = player?.hand_evs || []
  const hand_eqs = player?.hand_eqs || []
  const hand_eqrs = player?.hand_eqrs || []

  const minEqr = Math.min(...hand_eqrs)
  const maxEqr = Math.max(...hand_eqrs)

  const topHandRange = Math.max(...range)

  const allHandGroupRanges = getPlayerTopHandGroupRange(player?.simple_hand_counters)
  const topHandGroupRange = Math.max(...allHandGroupRanges)

  const res = {
    ...(player || {}),
    player: player?.player || {},
    position: player?.player?.position || player?.position || '',
    is_hero: player?.player?.is_hero || player?.is_hero,
    topHandRange,
    topHandGroupRange,
    range,
    hand_evs,
    hand_eqs,
    hand_eqrs,
    total_ev,
    total_eq,
    total_eqr,
    minEqr,
    maxEqr,
    simple_hand_counters: player?.simple_hand_counters || {},
    draw_categories: player?.draw_categories || [],
    hand_categories: player?.hand_categories || [],
    eq_percentile: player?.eq_percentile || [],
    relative_postflop_position: player?.relative_postflop_position || '',
    total_combos: player?.total_combos ?? null, // should be named total_range
    total_range_filtered: 0,
    tournament_evs_converter: player?.tournament_evs_converter || null,
  }
  // console.log('player', player?.is_hero, res)
  return res
}

/// TODO remove obe from below - its almost the same -- one returns total_frequency second strategy but its the same algo - decide which is better or optimize and replace on all those places
// not going to doit now since it works well but one is just redundant
function getCombosSolution({ combos, solution }) {
  // let now = Date.now()
  const heroPlayerIndex = 0
  const total_hand_range = combos.reduce(
    (a, combo) => a + combo.players[heroPlayerIndex].default.range,
    0,
  )
  const combosSolution = solution.map((a, indexAction) => {
    const actions = combos.map(combo => {
      const { strategy } = combo.solution[indexAction]
      const picked = {
        action_strategy_weighted: combo.players[heroPlayerIndex].default.range * strategy,
      }
      return picked
    })
    const total_action_weighted = actions.reduce((x, b) => x + b.action_strategy_weighted, 0)
    const strategy = total_action_weighted / (total_hand_range || 1)
    const mappedAction = {
      strategy,
      ...getActionPick(a),
    }
    return mappedAction
  })
  // console.log('getCombosSolution', Date.now() - now)
  return combosSolution
}

function getSolutionFrequency({ solution, selectedCombos }) {
  // let now = Date.now()
  const totalActionCombos = {} // { X:0, RAI:0 ...}
  solution.forEach(action => {
    totalActionCombos[action.code] = 0
  })

  let totalSelectedCombos = 0

  selectedCombos.forEach(combo => {
    solution.forEach((action, aIdx) => {
      const comboDefaultRange = combo.players[heroIndex]?.default?.range
      const comboActionStrategy = combo.solution[aIdx].strategy

      const rangeTimesStrategy = comboDefaultRange * comboActionStrategy

      totalActionCombos[action.code] += rangeTimesStrategy
      totalSelectedCombos += rangeTimesStrategy
    })
  })

  const updatedSolution = solution.map(action => {
    const totalPercentageOfCombo = totalActionCombos[action.code] / (totalSelectedCombos || 1)
    const total_frequency = totalPercentageOfCombo
    const mappedAction = {
      ...action,
      total_frequency,
    }
    return mappedAction
  })
  // console.log('getSolutionFrequency', Date.now() - now)
  return updatedSolution
}
///--------------------

function getRangeProps({ range, topRange }) {
  // sanitize range as it can be above 1 returned from API
  range = range > 1 ? 1 : range
  let rangeNrm = range / topRange

  // anything below 0.1% NORMALIZED range should get hidden
  // https://discord.com/channels/712710085125865612/1031522482663981116/1220689554390908960
  if (rangeNrm < 0.001) {
    range = 0
    rangeNrm = 0
  }
  const rangeFullHeight = range ? 1 : 0
  return {
    range, //
    range_normalized: rangeNrm,
    range_full_height: rangeFullHeight,
  }
}

function getTableGroup({ group, solution, players }) {
  const l_players = players.map(player => {
    const playerSHC = player?.simple_hand_counters?.[group.id]

    const { range, range_normalized, range_full_height } = getRangeProps({
      range: playerSHC?.total_frequency,
      topRange: player.topHandGroupRange,
    })
    return {
      range,
      range_normalized,
      range_full_height,
      ev: playerSHC?.hand_ev,
      eq: playerSHC?.hand_eq,
      eqr: playerSHC?.hand_eqr,
      default: {
        range,
        range_normalized,
        range_full_height,
        ev: playerSHC?.hand_ev,
        eq: playerSHC?.hand_eq,
        eqr: playerSHC?.hand_eqr,
        total_combos_available: playerSHC?.total_combos_available,
      },
    }
  })

  const heroPlayer = players[0]
  const groupSHC_solution = heroPlayer?.simple_hand_counters?.[group.id].actions_total_frequencies

  const mappedGroup = {
    id: group.id,
    table_index: group.table_index,
    combos: group.combos,
    is_locked: group.combos.some(cc => cc.is_locked),
    solution: solution
      ? solution.map(i => ({
          ...i,
          strategy: groupSHC_solution[i.code],
        }))
      : [],
    players: l_players,
  }
  return mappedGroup
}
function normalizePlayers({ items, playersTopRange }) {
  const res = items.map(cp => {
    cp.players = cp.players.map((player, idx) => {
      player.range_normalized = player.range / playersTopRange[idx]
      return player
    })
    return cp
  })
  return res
}

function updateCombos({
  combos,
  solution,
  players,
  handsLocked,
  handsIsometric,
  blockerRate,
  unblockerRate,
}) {
  const updatedCombos = combos.map((comboItem, idx) => {
    const comboSolution = solution.map(a => {
      const strategy = a.strategy?.[idx] || 0
      const action = {
        ev: a.evs?.[idx],
        eq: a.eqs?.[idx],
        strategy,
        ...getActionPick(a),
      }
      return action
    })

    const l_players = players.map(player => {
      const { range, range_normalized, range_full_height } = getRangeProps({
        range: player?.range?.[idx],
        topRange: player.topHandRange,
      })
      return {
        range,
        range_normalized,
        range_full_height,
        ev: player.hand_evs?.[idx],
        eq: player.hand_eqs?.[idx],
        eq_percentile: player.eq_percentile?.[idx],
        eqr: player.hand_eqrs?.[idx],
        blocker_rate: blockerRate?.[idx],
        unblocker_rate: unblockerRate?.[idx],
        eqr_clrscaled: getScaledValue({
          min: player.minEqr,
          max: player.maxEqr,
          value: player.hand_eqrs?.[idx],
        }),
        default: {
          range,
          range_normalized,
          range_full_height,
        },
      }
    })

    const combo = {
      ...comboItem,
      solution: comboSolution,
      is_locked: handsLocked?.[idx] || false,
      isometric_bucket: handsIsometric?.[idx],
      players: l_players,
    }

    return combo
  })
  return {
    combos: updatedCombos,
  }
}

function updateTableGroups({ tableGroups, combos, players, solution }) {
  let result = []
  if (combos && solution) {
    result = tableGroups.map(group => {
      const groupWithCombos = {
        ...group,
        combos: group.combos.map(combo => combos[combo.api_index]),
      }
      return getTableGroup({
        group: groupWithCombos,
        solution,
        players,
      })
    })
  } else {
    result = tableGroups.map(group => {
      const r = getTableGroup({
        group,
        solution,
        players,
      })
      return r
    })
  }

  return result
}

function getGroupValues({ combos, player, total_combos_available }) {
  const reducedValues = combos.reduce(
    (a, b) => {
      const playerInfo = b.players[player]
      const res = {
        range: a.range + playerInfo.range,
        ev: a.ev + playerInfo.ev * playerInfo.range,
        eq: a.eq + playerInfo.eq * playerInfo.range,
        eqr: a.eqr + playerInfo.eqr * playerInfo.range,
      }
      return res
    },
    {
      range: 0,
      ev: 0,
      eq: 0,
      eqr: 0,
    },
  )
  const range = reducedValues.range / (total_combos_available || 1)
  const ev = reducedValues.ev / (reducedValues.range || 1)
  const eq = reducedValues.eq / (reducedValues.range || 1)
  const eqr = reducedValues.eqr / (reducedValues.range || 1)
  return {
    range,
    ev,
    eq,
    eqr,
  }
}

// matches tohose hands across that are present in both category groups arrays
function getSharedItems(arrOfArrays) {
  const allItems = arrOfArrays.flat()
  const numOfOccurence = arrOfArrays.length
  if (numOfOccurence === 1) {
    return allItems
  }
  const ocurrences = countBy(allItems)
  const res = []
  Object.keys(ocurrences).forEach(key => {
    if (ocurrences[key] === numOfOccurence) {
      res.push(key)
    }
  })
  return res
}
function getComboActionsRange({ combo, filterActions }) {
  const selectedActions = combo.solution.filter(action =>
    filterActions.some(a => a.code === action.code),
  )
  const heroCombo = combo.players[0]
  const total_action_frequency_range = selectedActions.reduce(
    (a, b) => a + b.strategy * heroCombo.default.range,
    0,
  )
  const res = total_action_frequency_range
  return res
}

function getCombosGroupsByComboIdxRange({ categoriesRange, player, comboIdx }) {
  // const combos = categoriesRange.map((i, idx) => (i === comboIdx ? allCombos[idx] : null)).filter(i => i)
  const combos = categoriesRange
    .map((i, idx) => (i === comboIdx ? (player.range[idx] ? allCombos[idx] : null) : null))
    .filter(i => i)
  let groups = combos.map(cId => getComboMainGroup({ id: cId }).group_id)
  groups = [...new Set(groups)]
  return { groups, combos }
}
function getFilterCategories({ categoryId, categoriesRange, solution, players }) {
  const clrs = getCategoriesColors()

  const playersCategories = players.map(player => player[categoryId] || [])
  const playersCategoriesNames = playersCategories.flat().map(i => i.name)
  const allCategoryNames = [...new Set([...playersCategoriesNames])]

  let res = allCategoryNames.map(name => {
    const playersData = players.map((player, index) => {
      const playerCategory = playersCategories[index].find(i => i.name === name)

      const { groups: groupsPlayer, combos: combosPlayer } =
        playerCategory && (player.categoriesRange || categoriesRange)
          ? getCombosGroupsByComboIdxRange({
              player,
              comboIdx: playerCategory.index,
              categoriesRange: player.categoriesRange || categoriesRange,
            })
          : { groups: [], combos: [] }

      const playerData = {
        groups: groupsPlayer,
        combos: combosPlayer,
        total_range: playerCategory?.total_combos || null,
        total_occurrence: playerCategory?.total_frequency || null,
        total_occurrence_normalized: 1, // default value
      }

      if (player.is_hero) {
        playerData.solution =
          playerCategory && solution
            ? solution.map(a => ({
                ...getActionPick(a),
                total_frequency: playerCategory.actions_total_frequencies[a.code],
              }))
            : null
        playerData.total_frequencies = playerCategory?.actions_total_frequencies || null
      }

      return playerData
    })

    const categoryWithIndex = playersCategories
      .flatMap(cat => cat)
      .find(cat => cat?.name === name && cat?.index != null)

    // const categoryWithIndex = playersCategories.flatMap(cat => cat).find(cat => cat?.index != null)
    const categoryWithDisplayOrder = playersCategories
      .flatMap(cat => cat)
      .find(cat => cat?.name === name && cat?.display_order != null)

    const result = {
      name,
      categoryId,
      clr: clrs[name],
      index: categoryWithIndex?.index,
      display_order: categoryWithDisplayOrder?.display_order,
      players: playersData,
    }
    return result
  })
  res = res.sort((a, b) => (a.display_order > b.display_order ? 1 : -1))
  return res
}
function getEqBucketFilterCategory({ bucketId, hero, opponent, useDefault = false }) {
  let solutions = hero.buckets_solutions
  let heroBuckets = hero.buckets
  let opponentBuckets = opponent?.buckets
  if (useDefault) {
    solutions = hero.default.buckets_solutions
    heroBuckets = hero.default.buckets
    opponentBuckets = opponent?.default?.buckets
  }
  const combosHero = heroBuckets?.[bucketId]?.combos || []
  const combosOpponent = opponentBuckets?.[bucketId].combos || []

  const groupsHero = [...new Set((heroBuckets?.[bucketId]?.combos || []).map(i => i.group_id))]
  const groupsOpponent = [
    ...new Set((opponentBuckets?.[bucketId].combos || []).map(i => i.group_id)),
  ]

  const sol = (solutions || []).find(i => i.name === bucketId)

  const hero_total_range = heroBuckets?.total_range
  const opponent_total_range = opponentBuckets?.total_range
  const res = {
    default: useDefault,
    name: bucketId,
    categoryId: 'eq_buckets',
    hero_total_range,
    // hero_total_range_actions,
    opponent_total_range,
    hero: {
      groups: groupsHero,
      combos: combosHero,
      solution: sol?.solution || [],
      total_range: heroBuckets?.[bucketId]?.total_range,
      total_occurrence: heroBuckets?.[bucketId]?.total_range / hero_total_range,
    },
    opponent: {
      groups: groupsOpponent,
      combos: combosOpponent,
      total_range: opponentBuckets?.[bucketId]?.total_range,
      total_occurrence: opponentBuckets?.[bucketId]?.total_range / opponent_total_range,
    },
  }
  return res
}
function getEqBucketsSimpleCategory({ category_id, bucketId, hero, opponent }) {
  const bucketsHero = (hero[category_id] || []).find(i => i.name === bucketId)
  const bucketsOpponent = (opponent[category_id] || []).find(i => i.name === bucketId)
  const res = {
    name: bucketId,
    categoryId: 'eq_buckets',
    opponent_total_range: bucketsOpponent?.total_combos,
    hero: {
      total_occurrence: bucketsHero?.total_frequency,
    },
    opponent: {
      total_occurrence: bucketsOpponent?.total_frequency,
    },
  }
  return res
}
function getCategoryTotalOccurenceNormalized(category) {
  const occurrences = category.map(c => c.players[heroIndex].total_occurrence || 0)
  const max = Math.max(...occurrences)
  const res = category.map(i => {
    const total_occurrence_normalized = getPercentageOfMax({
      max,
      value: i.players[heroIndex].total_occurrence,
    })

    const updatedPlayer = { ...i.players[heroIndex], total_occurrence_normalized }
    const r = {
      ...i,
      players: [updatedPlayer, ...i.players.slice(1)],
    }
    return r
  })
  return res
}

export const getVisibleItems = (items, useTotalOccurenceNormalized) => {
  const rangeItems = useTotalOccurenceNormalized
    ? getCategoryTotalOccurenceNormalized(items)
    : items
  return rangeItems.filter(i => i.players[heroIndex].total_occurrence)
}

// filtering
function toggleCombos({ combos, filter }) {
  let hand_categories = []
  let draw_categories = []
  let equity_categories = []
  let equity_advanced_categories = []

  const filter_active = filter.status !== 'inactive'
  const filter_only_actions = filter.status === 'only_actions'
  const filter_actions_length = filter.actions.length

  const players_ids = Array.from({ length: MAX_NUM_OF_PLAYERS }, () => [])
  if (filter_active) {
    const suits_selected_ids = getSuitsHands(filter.suits)

    // Equity Bucket Categories
    const has_equity_buckets = filter.categories.some(i => i.categoryId === 'equity_buckets')
    if (has_equity_buckets) {
      equity_categories = filter.categories.filter(i => i.categoryId === 'equity_buckets')
    }

    // Equity Bucket Advanced Categories
    const has_equity_buckets_advanced = filter.categories.some(
      i => i.categoryId === 'equity_buckets_advanced',
    )
    if (has_equity_buckets_advanced) {
      equity_advanced_categories = filter.categories.filter(
        i => i.categoryId === 'equity_buckets_advanced',
      )
    }

    // Hand Categories
    const has_hand_categories = filter.categories.some(i => i.categoryId === 'hand_categories')
    if (has_hand_categories) {
      hand_categories = filter.categories.filter(i => i.categoryId === 'hand_categories')
    }

    // Draw Categories
    const has_draw_categories = filter.categories.some(i => i.categoryId === 'draw_categories')
    if (has_draw_categories) {
      draw_categories = filter.categories.filter(i => i.categoryId === 'draw_categories')
    }

    players_ids.forEach((_, index) => {
      let player_ids = []
      let hands_combos_player = []
      let draws_combos_player = []
      const bucket_combos_player = []
      let bucket_advanced_combos_player = []

      if (has_equity_buckets) {
        draws_combos_player = equity_categories.reduce(
          (a, b) => [...a, ...(b.players[index]?.combos || [])],
          [],
        )
      }

      if (has_equity_buckets_advanced) {
        bucket_advanced_combos_player = equity_advanced_categories.reduce(
          (a, b) => [...a, ...(b.players[index]?.combos || [])],
          [],
        )
      }

      if (has_hand_categories) {
        draws_combos_player = hand_categories.reduce(
          (a, b) => [...a, ...(b.players[index]?.combos || [])],
          [],
        )
      }

      if (has_draw_categories) {
        hands_combos_player = draw_categories.reduce(
          (a, b) => [...a, ...(b.players[index]?.combos || [])],
          [],
        )
      }

      player_ids = [
        suits_selected_ids,
        hands_combos_player,
        draws_combos_player,
        bucket_combos_player,
        bucket_advanced_combos_player,
      ]
      player_ids = player_ids.filter(i => i.length)

      if (filter.filter_intersection) {
        player_ids = getSharedItems(player_ids)
      } else {
        player_ids = player_ids.flat()
      }

      players_ids[index] = player_ids
    })
  }

  const playersRange = Array.from({ length: MAX_NUM_OF_PLAYERS }, () => [])
  let playersTotalRange = Array.from({ length: MAX_NUM_OF_PLAYERS }, () => 0)
  const playersSelectedCombosIds = Array.from({ length: MAX_NUM_OF_PLAYERS }, () => [])

  const heroCombosIgnoreActions = []
  let heroCombosIgnoreActionsTotalRange = 0

  combos = combos.map(cp => {
    if (filter_active) {
      cp.players?.forEach((_, index) => {
        let range_actions = 0

        const playerIsHero = index === 0 // first player is always hero
        const player_ids_length = players_ids[index].length
        let player_selected = filter_only_actions // when filtering only-actions all combos are selected

        if (player_ids_length) {
          player_selected = players_ids[index]?.includes(cp.id)
        }

        if (filter_actions_length) {
          range_actions = getComboActionsRange({
            combo: cp,
            filterActions: filter.actions,
          })
        }

        if (playerIsHero) {
          if (!player_selected) {
            cp.players[index].range = 0
            cp.players[index].range_full_height = 0
          } else if (cp.players[index].default.range) {
            heroCombosIgnoreActions.push(cp)
            heroCombosIgnoreActionsTotalRange += cp.players[index].default.range

            if (filter_actions_length) {
              cp.players[index].range = range_actions
            } else {
              cp.players[index].range = cp.players[index].default.range
            }
            if (cp.players[index].range) {
              cp.players[index].range_full_height = cp.players[index].default.range_full_height

              playersRange[index]?.push(cp.players[index].range)
              playersTotalRange[index] += cp.players[index].range
            }
          }
        }

        if (!playerIsHero) {
          if (!player_selected) {
            cp.players[index].range = 0
            cp.players[index].range_full_height = 0
          } else if (cp.players[index].default.range) {
            cp.players[index].range = cp.players[index].default.range
            cp.players[index].range_full_height = cp.players[index].default.range_full_height

            if (!filter_only_actions) {
              playersSelectedCombosIds[index].push(cp.id)
            }

            playersRange[index]?.push(cp.players[index].range)
            playersTotalRange[index] += cp.players[index].range
          }
        }
      })
    } else {
      cp.players?.forEach((_, index) => {
        cp.players[index].range = cp.players[index].default.range
        cp.players[index].range_normalized = cp.players[index].default.range_normalized
        cp.players[index].range_full_height = cp.players[index].default.range_full_height
      })
    }

    return cp
  })

  if (filter_active) {
    const playersTopRange = playersRange.map(getMaxRange)
    combos = normalizePlayers({
      items: combos,
      playersTopRange,
    })
  }

  const res = {
    combos,
    playersSelectedCombosIds,
    playersTotalRange,
    heroCombosIgnoreActions,
    heroCombosIgnoreActionsTotalRange,
  }
  return res
}
function toggleTableGroups({
  tableGroups,
  filter,
  solution,
  // combos,
}) {
  const filter_active = filter.status !== 'inactive'
  const filter_actions_length = filter.actions.length
  const filter_active_rules = filter.active_rules

  let heroSelectedCombosIds = []

  const playersRange = Array.from({ length: MAX_NUM_OF_PLAYERS }, () => [])

  tableGroups = tableGroups.map(group => {
    group.players = group.players?.map((player, playerIndex) => {
      const playerIsHero = playerIndex === 0
      const combosPlayer = filter_active_rules
        ? group.combos.filter(i => i.players[playerIndex].range)
        : group.combos
      const combos_length = combosPlayer.length

      if (playerIsHero) {
        const groupSolution = getCombosSolution({ combos: combosPlayer, solution })
        let aboveTotalStrategyLimit = true
        if (filter_actions_length) {
          const activeActions = groupSolution.filter(as =>
            filter.actions.some(a => a.code === as.code),
          )
          const totalActiveStrategy = activeActions.reduce((a, b) => a + b.strategy, 0)
          aboveTotalStrategyLimit = totalActiveStrategy >= 0.0009
        }

        group.solution = groupSolution

        if (filter_active_rules && combos_length && aboveTotalStrategyLimit) {
          const { range, ev, eq, eqr } = getGroupValues({
            combos: combosPlayer,
            player: playerIndex,
            total_combos_available: player.default.total_combos_available,
          })
          player.range = range
          player.range_full_height = range ? 1 : 0
          player.ev = ev
          player.eq = eq
          player.eqr = eqr

          const groupCombosIds = combosPlayer.map(cp => cp.id)
          heroSelectedCombosIds = [...heroSelectedCombosIds, ...groupCombosIds]

          playersRange[playerIndex].push(player.range)
        } else if (filter_active_rules) {
          player.range = 0
          player.range_normalized = 0
          player.range_full_height = 0
          player.ev = 0
          player.eq = 0
          player.eqr = 0
        } else {
          player.range = player.default.range
          player.range_normalized = player.default.range_normalized
          player.range_full_height = player.default.range_full_height
          player.ev = player.default.ev
          player.eq = player.default.eq
          player.eqr = player.default.eqr
        }
      }

      if (!playerIsHero) {
        if (filter_active_rules && combos_length) {
          const { range, ev, eq, eqr } = getGroupValues({
            combos: combosPlayer,
            player: playerIndex,
            total_combos_available: player.default.total_combos_available,
          })
          player.range = range
          player.range_full_height = range ? 1 : 0
          player.ev = ev
          player.eq = eq
          player.eqr = eqr

          playersRange[playerIndex].push(player.range)
        } else if (filter_active_rules) {
          player.range = 0
          player.range_normalized = 0
          player.range_full_height = 0
          player.ev = 0
          player.eq = 0
          player.eqr = 0
        } else {
          player.range = player.default.range
          player.range_normalized = player.default.range_normalized
          player.range_full_height = player.default.range_full_height
          player.ev = player.default.ev
          player.eq = player.default.eq
          player.eqr = player.default.eqr
        }
      }
      return player
    })

    return group
  })

  if (filter_active) {
    const playersTopRange = playersRange.map(getMaxRange)

    tableGroups = normalizePlayers({
      items: tableGroups,
      playersTopRange,
    })
  }

  return {
    tableGroups,
    playersRange,
    heroSelectedCombosIds,
  }
}

// this stack creates 1326 hands from 169 preflop combos
function getPreflopComboByGroup(groupVals, hasMinusCheck) {
  if (hasMinusCheck) {
    // can happen on preflop that somehin like -0,000404 is returned -- BE will perform this check in da future
    return allCombosGroupsIdxs.map(idx => (groupVals[idx] < 0 ? 0 : groupVals[idx] || 0))
  }
  return allCombosGroupsIdxs.map(idx => groupVals[idx] || 0)
}

function getPreflopPlayer(player) {
  return {
    ...player,
    eq_percentile: getPreflopComboByGroup(player.eq_percentile || []),
    hand_eqs: getPreflopComboByGroup(player.hand_eqs || []),
    hand_eqrs: getPreflopComboByGroup(player.hand_eqrs || []),
    hand_evs: getPreflopComboByGroup(player.hand_evs || []),
    range: getPreflopComboByGroup(player.range || []),
  }
}
function getPreflopActionSolution({ evs, strategy }) {
  return {
    // ...action,
    evs: getPreflopComboByGroup(evs || []),
    strategy: getPreflopComboByGroup(strategy || [], 'hasMinusCheck'),
  }
}
function getApiPreflopSolutionData(apiData) {
  // console.log(apiData.players_info[0].range)
  if (apiData.players_info[0].range.length > 169) {
    // console.log('preflop data is already in correct format')
    return apiData
  }
  // console.log('preflop data needs to be converted')

  const res = {
    ...apiData,
    players_info: apiData.players_info.map(player => getPreflopPlayer(player)),
    solutions: apiData.solutions.map(action => ({
      ...action,
      // trainer has full_solution -
      ...getPreflopActionSolution({
        evs: action.evs || action.full_solution.evs,
        strategy: action.strategy || action.full_solution.strategy,
      }),
    })),
  }
  // console.log(res)
  return res
}

// --sol data helpers
function getHspotFromApisolutions({ game, available_actions }) {
  const spot = {
    available_actions,
    game,
  }
  let res = getHistorySpots({
    history_spots: [spot],
  })
  res = res[0]
  res.id = 'sol_spot_dontuseid'
  return res
}
function getSolCategories({ solData, solution, players }) {
  const getFilterCat = (key, hasPlayers) => {
    const playersWithCategoriesRange = players.map(player => ({
      ...player,
      categoriesRange: hasPlayers ? player[`${key}_range`] : null,
    }))
    return getFilterCategories({
      categoryId: key,
      categoriesRange: !hasPlayers ? solData[`${key}_range`] : null,
      solution,
      players: playersWithCategoriesRange,
    })
  }
  const res = {
    catDraws: getFilterCat('draw_categories', false),
    catHands: getFilterCat('hand_categories', false),
    catEqBuckets: getFilterCat('equity_buckets', true),
    catEqBucketsAdvanced: getFilterCat('equity_buckets_advanced', true),
  }
  return res
}

function getSolProps({ apiData, combos, handsIsometric, tableGroups }) {
  const solData = getApiPreflopSolutionData(apiData)
  const available_actions = getAvailableActionsFromApiSolutions({ solData })

  const solution = getSolution({ solution: solData.solutions, available_actions })
  const solution_picked = getSolutionPick({ solution })

  // sort players so hero is first
  const sortedPlayers = solData.players_info.sort(i => (i.player.is_hero ? -1 : 1))
  const players = [
    getSolPlayer({ player: sortedPlayers[0] }),
    getSolPlayer({ player: sortedPlayers[1] }),
    ...Array.from({ length: MAX_NUM_OF_PLAYERS - 2 }, (_, index) =>
      sortedPlayers[index + 2] ? getSolPlayer({ player: sortedPlayers[index + 2] }) : null,
    ),
  ].filter(Boolean)

  const blockerRate = solData.blocker_rate || []
  const unblockerRate = solData.unblocker_rate || []
  const handsLocked = solData.hands_locked || []

  const { combos: l_combos } = updateCombos({
    combos,
    solution,
    players,
    blockerRate,
    unblockerRate,
    handsLocked,
    handsIsometric,
  })

  const l_tableGroups = updateTableGroups({
    tableGroups,
    combos: l_combos,
    solution,
    players,
  })

  const categories = getSolCategories({
    solData,
    solution,
    players,
  })

  const { catDraws } = categories
  const { catHands } = categories
  const { catEqBuckets } = categories
  const { catEqBucketsAdvanced } = categories

  const evProps = {
    ...players.reduce(
      (acc, player, index) => ({
        ...acc,
        [`${index}_total_ev`]: player.total_ev,
      }),
      {},
    ),
  }
  const res = {
    solData,
    solution,
    solution_picked,
    tableGroups: l_tableGroups,
    combos: l_combos,
    players,
    evProps,
    catDraws,
    catHands,
    catEqBuckets,
    catEqBucketsAdvanced,
  }

  return res
}
function toggleSolProps({ filter, solution, combos, tableGroups }) {
  const {
    combos: l_combos,
    playersSelectedCombosIds,
    playersTotalRange,
    heroCombosIgnoreActions,
    heroCombosIgnoreActionsTotalRange,
  } = toggleCombos({
    combos,
    filter,
  })

  const { tableGroups: l_tableGroups, heroSelectedCombosIds } = toggleTableGroups({
    tableGroups,
    solution,
    filter,
  })

  // set hero selected combo to first index to have all selected combos in one array
  playersSelectedCombosIds[0] = heroSelectedCombosIds

  const recomputeFreqCombos = filter.status !== 'inactive' && filter.status !== 'only_actions'

  let solution_picked = null
  if (recomputeFreqCombos) {
    solution = getSolutionFrequency({
      solution,
      selectedCombos: heroCombosIgnoreActions,
    })
    solution_picked = getSolutionPick({ solution })
    solution_picked = solution_picked.map(a => {
      const res = {
        ...a,
        total_combos: heroCombosIgnoreActionsTotalRange * a.total_frequency,
      }
      return res
    })
  } else {
    solution_picked = getSolutionPick({ solution })
  }

  const res = {
    solution_picked,
    combos: l_combos,
    tableGroups: l_tableGroups,
    playersSelectedCombosIds,
    heroSelectedCombosIds,
    playersTotalRange,
  }
  return res
}

function getSolViewProps({
  sol_data_updated,
  sol_data_street,
  sol_detail_player,
  sol_data_players,
  sol_elite_is_using,
  showHandValue,
  sw_tabs_study,
  sw_tabs_strategy,
  sw_range_content_hero,
  sw_hand_content,
  sw_range_content_opponent,
  sw_range_height,
  sw_combos_columns,
  sw_combos_chart_style,
  sw_ev_converter,
}) {
  const soltab = sw_tabs_study
  const showFlopContent = sol_elite_is_using || sol_data_street !== 'preflop'

  const player = sol_detail_player

  // TODO: rm after 3way rls
  let hero_showPropTable =
    soltab === 'strategy' ? sw_tabs_strategy : sw_range_content_hero.replace('swv_', '')
  const heroShowPropTableDict = { overview_range: 'range', overview_all: 'strategy' }
  hero_showPropTable = heroShowPropTableDict[hero_showPropTable] || hero_showPropTable
  const hero_emptyTable =
    !showFlopContent && ['eq', 'strategy_eq', 'eqr'].includes(hero_showPropTable)

  const hero_noValueTable = hero_emptyTable || ['strategy'].includes(hero_showPropTable)
  const hero_valuePropTable = hero_showPropTable.includes('strategy_')
    ? hero_showPropTable.replace('strategy_', '')
    : ['strategy', 'ev_compare'].includes(hero_showPropTable)
      ? 'ev'
      : hero_showPropTable
  let hero_showPropHands =
    soltab === 'breakdown'
      ? sw_hand_content.replace('swv_', '')
      : // 'strategy'
        soltab === 'strategy'
        ? sw_tabs_strategy
        : sw_range_content_hero.replace('swv_', '')
  const heroShowPropHandsDict = { overview_range: 'range' }
  hero_showPropHands = heroShowPropHandsDict[hero_showPropHands] || hero_showPropHands

  const opponent_showPropTable = sw_range_content_opponent.replace('swv_', '')
  const opponent_emptyTable =
    !showFlopContent && ['ev', 'eq', 'strategy_eq', 'eqr'].includes(opponent_showPropTable)

  const opponent_noValueTable = opponent_emptyTable
  const opponent_valuePropTable = opponent_showPropTable
  const opponent_valuePropHands = opponent_showPropTable

  const rangeFull = sw_range_height === 'swv_range_full'
  const rangeNormalized = sw_range_height === 'swv_range_normalized'
  const rangeKey = rangeFull ? 'range_full_height' : rangeNormalized ? 'range_normalized' : 'range'

  const res = {
    study_range_full_height: rangeFull,
    study_range_normalized: rangeNormalized,
    rangeKey,

    player,

    evComparePickerVisible: soltab === 'strategy' && hero_showPropTable === 'ev_compare',

    hero_showPropTable,
    hero_emptyTable,
    hero_noValueTable: !showHandValue || hero_noValueTable,
    hero_valuePropTable,
    hero_showPropHands,

    opponent_emptyTable,
    opponent_showPropTable,
    opponent_noValueTable: !showHandValue || opponent_noValueTable,
    opponent_valuePropTable,
    opponent_valuePropHands,

    study_combos_columns: sw_combos_columns === 'swv_combos_horizontal',
    combos_columns_line: sw_combos_chart_style === 'swv_combos_chart_line',
    study_solution_tab: soltab,

    // TODO: rm after 3way rls
    player_showPropHands: player === '0' ? hero_showPropHands : opponent_valuePropHands,
    player_emptyHands: player === '0' ? hero_emptyTable : opponent_emptyTable,

    heroLeft:
      !sol_data_players()[1]?.position ||
      sol_data_players()[0].relative_postflop_position === 'OOP',
    study_solution_tabs_fullscreen: false,

    sol_data_updated,
  }

  // console.log('sol_view_viewProps', res)
  return res
}

export {
  getComboActionsRange,
  getSharedItems,
  combosSkeleton,
  rangesDataSkeleton,
  rangeTableGroups,
  getComboMainGroup,
  getSolution,
  getSolutionPick,
  getAvailableActionsFromApiSolutions,
  updateCombos,
  getTableGroup,
  getCombosSolution,
  getGroupValues,
  updateTableGroups,
  getSolPlayer,
  getSolutionFrequency,
  getFilterCategories,
  getCombosGroupsByComboIdxRange,
  getEqBucketFilterCategory,
  getEqBucketsSimpleCategory,
  getCategoryTotalOccurenceNormalized,
  getSuitsHands,
  toggleCombos,
  toggleTableGroups,
  getScaledValue,
  getAbsoluteEvRatio,
  getEvDistanceFromAvgEv,
  getEvClr,
  getEqClr,
  eqrClr,
  getEvCompareByPot,
  getComboEvCompareClr,
  getEvTopAction,
  getEvCompareClr,
  getStrategyClrs,
  getComboStrategyClrs,
  getEvClrs,
  getEvCompareClrs,
  getEqClrs,
  getEqrClrs,
  getBlockerClrs,
  getEvScaledClr,
  getScaledClr,
  getApiPreflopSolutionData,
  getHspotFromApisolutions,
  getSolCategories,
  getSolProps,
  toggleSolProps,
  getSolViewProps,
}

/*
import allGroups from './allGroups.json'
function getGroupsCombosIndexes() {
  // eslint-disable-next-line no-use-before-define
  const combos = combosSkeleton()
  const res = combos.map(combo => {
    const r = allGroups.findIndex(cId => cId === combo.group_id)
    return r
  })
  return res
}
console.log(getGroupsCombosIndexes())
console.log(allCombosGroupsIdxs)
*/
/*
function getSummaryTableGroups() {
  return [
    'AA', 'AKs', 'AQs', 'AJs', 'ATs', 'A9s', 'A8s', 'A7s', 'A6s', 'A5s', 'A4s', 'A3s', 'A2s',
    'AKo',
    'AQo',
    'AJo',
    'ATo',
    'A9o',
    'A8o',
    'A7o',
    'A6o',
    'A5o',
    'A4o',
    'A3o',
    'A2o',
    'KK', 'KQs', 'KJs', 'KTs', 'K9s', 'K8s', 'K7s', 'K6s', 'K5s', 'K4s', 'K3s', 'K2s',
    'KQo',
    'KJo',
    'KTo',
    'K9o',
    'K8o',
    'K7o',
    'K6o',
    'K5o',
    'K4o',
    'K3o',
    'K2o',
    'QQ', 'QJs', 'QTs', 'Q9s', 'Q8s', 'Q7s', 'Q6s', 'Q5s', 'Q4s', 'Q3s', 'Q2s',
    'QJo',
    'QTo',
    'Q9o',
    'Q8o',
    'Q7o',
    'Q6o',
    'Q5o',
    'Q4o',
    'Q3o',
    'Q2o',
    'JJ', 'JTs', 'J9s', 'J8s', 'J7s', 'J6s', 'J5s', 'J4s', 'J3s', 'J2s',
    'JTo',
    'J9o',
    'J8o',
    'J7o',
    'J6o',
    'J5o',
    'J4o',
    'J3o',
    'J2o',
    'TT', 'T9s', 'T8s', 'T7s', 'T6s', 'T5s', 'T4s', 'T3s', 'T2s',
    'T9o',
    'T8o',
    'T7o',
    'T6o',
    'T5o',
    'T4o',
    'T3o',
    'T2o',
    '99', '98s', '97s', '96s', '95s', '94s', '93s', '92s',
    '98o',
    '97o',
    '96o',
    '95o',
    '94o',
    '93o',
    '92o',
    '88', '87s', '86s', '85s', '84s', '83s', '82s',
    '87o',
    '86o',
    '85o',
    '84o',
    '83o',
    '82o',
    '77', '76s', '75s', '74s', '73s', '72s',
    '76o',
    '75o',
    '74o',
    '73o',
    '72o',
    '66', '65s', '64s', '63s', '62s',
    '65o',
    '64o',
    '63o',
    '62o',
    '55', '54s', '53s', '52s',
    '54o',
    '53o',
    '52o',
    '44', '43s', '42s',
    '43o',
    '42o',
    '33', '32s',
    '32o',
    '22',
  ]
}

function getSummaryTableCombosIndexes() {
  const summaryTableGroups = getSummaryTableGroups()
  const { tableGroups } = rangesDataSkeleton()
  const combosSummaryTable = summaryTableGroups.reduce((a, groupId) => {
    const rtcGroup = tableGroups.find(g => g.id === groupId)
    const rtCombos = rtcGroup.combos.map(i => i.id)
    return [...a, ...rtCombos]
  }, [])
  const res = allCombos.map(comboId => {
    const idx = combosSummaryTable.findIndex(cId => cId === comboId)
    // return { [comboId]: idx }
    return idx
  })
  return res
}
console.log(getSummaryTableCombosIndexes())
// console.log(allCombosSummaryIdxs)
*/
